/**
 * Distance calculation utilities for GPS coordinates
 */

import { GpsPoint } from '../domain/telemetry/processed/GpsPoint'

/**
 * Calculates the distance between two GPS coordinates using the Haversine formula
 * @param lat1 Latitude of first point in decimal degrees
 * @param lon1 Longitude of first point in decimal degrees
 * @param lat2 Latitude of second point in decimal degrees
 * @param lon2 Longitude of second point in decimal degrees
 * @returns Distance in meters
 */
export function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371e3 // Earth's radius in meters
  const φ1 = (lat1 * Math.PI) / 180
  const φ2 = (lat2 * Math.PI) / 180
  const Δφ = ((lat2 - lat1) * Math.PI) / 180
  const Δλ = ((lon2 - lon1) * Math.PI) / 180

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))

  return R * c
}

/**
 * Calculates distance between two telemetry points
 */
export function calculatePointDistance(
  point1: GpsPoint,
  point2: GpsPoint
): number {
  return calculateDistance(
    point1.latitude,
    point1.longitude,
    point2.latitude,
    point2.longitude
  )
}

/**
 * Rounds distance to nearest interval (for indexing)
 * @param distance Distance in meters
 * @param interval Rounding interval in meters (default 5)
 * @returns Rounded distance
 */
export function roundDistance(distance: number, interval: number = 5): number {
  return Math.round(distance / interval) * interval
}

/**
 * Finds the closest point in a sorted array of telemetry points
 * @param targetDistance Target distance to find
 * @param points Array of telemetry points sorted by distance
 * @param lastIndex Last found index (optimization hint)
 * @param searchWindow Search window size
 * @returns Object containing closest point and its index
 */
export function findClosestPointByDistance(
  targetDistance: number,
  points: GpsPoint[],
  lastIndex: number = 0,
  searchWindow: number = 50
): { point: GpsPoint; index: number } {
  const startIndex = Math.max(0, lastIndex)
  const endIndex = Math.min(points.length, startIndex + searchWindow)

  let closestPoint = points[startIndex]
  let smallestDiff = Math.abs(
    points[startIndex].distanceMeters - targetDistance
  )
  let closestIndex = startIndex

  for (let i = startIndex + 1; i < endIndex; i++) {
    const diff = Math.abs(points[i].distanceMeters - targetDistance)
    if (diff < smallestDiff) {
      smallestDiff = diff
      closestPoint = points[i]
      closestIndex = i
    }
    // If we're past the target and difference is increasing, we can stop
    if (points[i].distanceMeters > targetDistance && diff > smallestDiff) {
      break
    }
  }

  return { point: closestPoint, index: closestIndex }
}

/**
 * Recalibrates distance values from one lap to match another lap's distance scale
 * This is essential for comparing laps that may have slightly different GPS measurements
 *
 * @param sourcePoints Points to recalibrate
 * @param referencePoints Reference lap points
 * @returns Recalibrated points with distances matched to reference lap
 */
export function recalibrateDistances(
  sourcePoints: GpsPoint[],
  referencePoints: GpsPoint[]
): GpsPoint[] {
  const recalibrated: GpsPoint[] = []
  let lastRefIndex = 0

  for (const sourcePoint of sourcePoints) {
    // Find closest point in reference lap by geographic position
    const { point: refPoint, index: refIndex } = findClosestPointByGeo(
      sourcePoint,
      referencePoints,
      lastRefIndex
    )
    lastRefIndex = refIndex

    // Create new point with recalibrated distance
    recalibrated.push({
      ...sourcePoint,
      distanceMeters: refPoint.distanceMeters,
    })
  }

  return recalibrated
}

/**
 * Fast squared distance calculation for comparing distances (avoids sqrt).
 * Uses equirectangular approximation which is accurate for small distances.
 * Much faster than Haversine for comparison purposes.
 */
function fastSquaredDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  // Equirectangular approximation - fast and accurate for small distances
  const x = (lon2 - lon1) * Math.cos((lat1 + lat2) * 0.00872664626) // 0.5 * PI/180
  const y = lat2 - lat1
  return x * x + y * y
}

/**
 * Finds closest point by geographic proximity using fast squared distance.
 * OPTIMIZED: Uses equirectangular approximation and early termination.
 */
export function findClosestPointByGeo(
  target: GpsPoint,
  points: GpsPoint[],
  startIndex: number = 0,
  searchWindow: number = 100
): { point: GpsPoint; index: number } {
  const start = Math.max(0, startIndex)
  const end = Math.min(points.length, start + searchWindow)

  if (start >= points.length) {
    return { point: points[points.length - 1], index: points.length - 1 }
  }

  const targetLat = target.latitude
  const targetLon = target.longitude

  let closestPoint = points[start]
  let smallestDistSq = fastSquaredDistance(
    targetLat,
    targetLon,
    closestPoint.latitude,
    closestPoint.longitude
  )
  let closestIndex = start

  // If we're very close already (within ~1m), return early
  // 1m ≈ 0.00001 degrees, squared = 1e-10
  if (smallestDistSq < 1e-10) {
    return { point: closestPoint, index: closestIndex }
  }

  for (let i = start + 1; i < end; i++) {
    const p = points[i]
    const distSq = fastSquaredDistance(
      targetLat,
      targetLon,
      p.latitude,
      p.longitude
    )

    if (distSq < smallestDistSq) {
      smallestDistSq = distSq
      closestPoint = p
      closestIndex = i

      // Early termination if very close
      if (distSq < 1e-10) break
    }
  }

  return { point: closestPoint, index: closestIndex }
}

/**
 * Result of recalibrating a single GPS point
 */
export interface RecalibratedPoint {
  /** The original GPS point */
  originalPoint: GpsPoint
  /** Distance recalibrated to match reference lap */
  recalibratedDistance: number
  /** Original distance before recalibration */
  originalDistance: number
}

/**
 * Create a distance recalibration function for aligning laps across different sessions.
 *
 * When comparing laps from different sessions, GPS measurements may vary slightly,
 * causing the same physical location on the track to have different distance values.
 * This function creates a lookup that maps each point to its geographically closest
 * point on the reference lap, using the reference lap's distance scale.
 *
 * @param referencePoints - GPS points from the reference lap (typically the fastest)
 * @returns A function that recalibrates distances for any target lap's points
 *
 * @example
 * ```typescript
 * const recalibrate = createDistanceRecalibrator(referenceLap.getGpsPoints())
 * const targetDataPoints = targetLap.getGpsPoints().map(point => {
 *   const recalibrated = recalibrate(point)
 *   return {
 *     distance: recalibrated.recalibratedDistance,
 *     speed: point.speedKmh,
 *     // ... other fields
 *   }
 * })
 * ```
 */
export function createDistanceRecalibrator(
  referencePoints: GpsPoint[]
): (
  targetPoint: GpsPoint,
  lastIndex?: number
) => { recalibratedDistance: number; newIndex: number } {
  return (targetPoint: GpsPoint, lastIndex: number = 0) => {
    const { point: closestRef, index: newIndex } = findClosestPointByGeo(
      targetPoint,
      referencePoints,
      lastIndex,
      100 // Search window
    )
    return {
      recalibratedDistance: closestRef.distanceMeters,
      newIndex,
    }
  }
}

/**
 * OPTIMIZED: Batch recalibrate all points from a target lap in a single pass.
 * This is much faster than calling createDistanceRecalibrator per-point because:
 * 1. No function call overhead per point
 * 2. Reference points array is accessed directly
 * 3. Coordinates are cached in local variables
 *
 * @param targetPoints - GPS points from the lap to recalibrate
 * @param referencePoints - GPS points from the reference lap
 * @returns Array of recalibrated distances (same order as targetPoints)
 */
export function batchRecalibrateDistances(
  targetPoints: GpsPoint[],
  referencePoints: GpsPoint[]
): number[] {
  if (referencePoints.length === 0) {
    return targetPoints.map((p) => p.distanceMeters)
  }

  const result: number[] = new Array(targetPoints.length)
  let lastIndex = 0
  const searchWindow = 100
  const refLength = referencePoints.length

  for (let t = 0; t < targetPoints.length; t++) {
    const target = targetPoints[t]
    const targetLat = target.latitude
    const targetLon = target.longitude

    // Find closest reference point using sliding window
    const start = Math.max(0, lastIndex - 20) // Allow some backward search
    const end = Math.min(refLength, start + searchWindow)

    let closestIndex = start
    let closestRef = referencePoints[start]
    let smallestDistSq = fastSquaredDistance(
      targetLat,
      targetLon,
      closestRef.latitude,
      closestRef.longitude
    )

    for (let i = start + 1; i < end; i++) {
      const ref = referencePoints[i]
      const distSq = fastSquaredDistance(
        targetLat,
        targetLon,
        ref.latitude,
        ref.longitude
      )

      if (distSq < smallestDistSq) {
        smallestDistSq = distSq
        closestRef = ref
        closestIndex = i

        // Early termination if very close (~1m)
        if (distSq < 1e-10) break
      }
    }

    result[t] = closestRef.distanceMeters
    lastIndex = closestIndex
  }

  return result
}

/**
 * ULTRA-OPTIMIZED: Batch recalibrate using raw coordinate arrays.
 * Avoids all object property access overhead by working with flat arrays.
 *
 * @param targetLats - Array of target latitudes
 * @param targetLons - Array of target longitudes
 * @param refLats - Array of reference latitudes
 * @param refLons - Array of reference longitudes
 * @param refDistances - Array of reference distances
 * @returns Array of recalibrated distances
 */
export function batchRecalibrateRaw(
  targetLats: number[],
  targetLons: number[],
  refLats: number[],
  refLons: number[],
  refDistances: number[]
): number[] {
  const targetLen = targetLats.length
  const refLen = refLats.length

  if (refLen === 0) {
    return new Array(targetLen).fill(0)
  }

  const result = new Array<number>(targetLen)
  let lastIndex = 0
  const searchWindow = 100

  for (let t = 0; t < targetLen; t++) {
    const tLat = targetLats[t]
    const tLon = targetLons[t]

    // Sliding window search
    const start = Math.max(0, lastIndex - 20)
    const end = Math.min(refLen, start + searchWindow)

    let closestIndex = start
    // Inline fast squared distance calculation
    let dx =
      (refLons[start] - tLon) *
      Math.cos((tLat + refLats[start]) * 0.00872664626)
    let dy = refLats[start] - tLat
    let smallestDistSq = dx * dx + dy * dy

    for (let i = start + 1; i < end; i++) {
      dx = (refLons[i] - tLon) * Math.cos((tLat + refLats[i]) * 0.00872664626)
      dy = refLats[i] - tLat
      const distSq = dx * dx + dy * dy

      if (distSq < smallestDistSq) {
        smallestDistSq = distSq
        closestIndex = i
        if (distSq < 1e-10) break
      }
    }

    result[t] = refDistances[closestIndex]
    lastIndex = closestIndex
  }

  return result
}

/**
 * Recalibrate all points from a target lap to match a reference lap's distance scale.
 *
 * This uses GPS coordinates to find geographically closest points between laps,
 * ensuring that the same physical location on the track has the same distance value
 * regardless of which lap/session it came from.
 *
 * @param targetPoints - GPS points from the lap to recalibrate
 * @param referencePoints - GPS points from the reference lap
 * @returns Array of recalibrated points with both original and aligned distances
 */
export function recalibratePointsToReference(
  targetPoints: GpsPoint[],
  referencePoints: GpsPoint[]
): RecalibratedPoint[] {
  const recalibrate = createDistanceRecalibrator(referencePoints)
  let lastIndex = 0

  return targetPoints.map((point) => {
    const { recalibratedDistance, newIndex } = recalibrate(point, lastIndex)
    lastIndex = newIndex

    return {
      originalPoint: point,
      recalibratedDistance,
      originalDistance: point.distanceMeters,
    }
  })
}
